import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dayjs } from 'dayjs';
import styled from 'styled-components';
import { Button, DayPicker, H3, Icon, Link, Text, Wrapper } from 'components';
import { IWeekView } from 'components/Calendar/DayPicker';
import { lineHeight, theme, toRem } from 'design';
import { useDayPayPeriod } from 'hooks';
import { routeConstants } from 'routes';
import { browserHistory } from 'routes/Router';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { CalculatorRules } from 'store/CalculatorRules';
import { getSitePaymentsRequest, Payments, selectPaymentSetsForSite } from 'store/Collections';
import { DaySegments, getDaySegmentsRequest, selectDaySegments } from 'store/Collections/DaySegments';
import { selectHasIntegration, selectSiteSettings } from 'store/Site/selectors';
import { IStore } from 'store/types';
import { ApiQueryOperator, Days, IDaySegment, IEmployerSite, IntegrationType, IPaymentSet, IPayPeriod, PayoutRefType, PermissionType } from 'types';
import { formatDateForApi, formatTimeRange, hasSitePermission, isDatePayable, isPayPeriodClosed } from 'utils';

interface IDateSelectorProps {
  currentSite: IEmployerSite;
  currentSiteId: string;
  onNext: (day: Dayjs, segments: IDaySegment[], payPeriod?: IPayPeriod) => void;
  pageHeaderContent: React.ReactNode;
}

const WeeklyToDailyBreakpoint = 870;

const CalendarWrapper = styled.section`
  display: flex;
  flex-flow: column nowrap;
  padding: 0 ${toRem(theme.space.md)};

  @media (max-width: ${theme.breakpoint.small}px) {
    width: 100%;
  }
`;

const ContentWrapper = styled.div<{ hasHeader?: boolean }>`
  display: flex;
  flex-flow: column nowrap;
  background-color: ${theme.color.neutral[50]};
  border-radius: ${({ hasHeader }) => (hasHeader ? `0 0 ${toRem(8)} ${toRem(8)}` : toRem(8))};
  padding: ${toRem(theme.space.xl)};
  width: 100%;
`;

const SelectedDateContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  a {
    font-weight: 500;
    text-decoration: none;
    font-size: ${toRem(theme.font.size.xs)};
    line-height: ${lineHeight(theme.font.size.xs)};
  }

  @media (max-width: ${WeeklyToDailyBreakpoint}px) {
    flex-flow: column nowrap;

    h3 {
      margin-bottom: ${toRem(theme.space.sm)};
    }
  }
`;

const DaySegmentHeader = styled.div`
  display: flex;
  justify-content: space-between;

  p {
    color: ${theme.color.neutral[500]};
  }
`;

const DaySegment = styled.div`
  display: flex;
  justify-content: space-between;
  padding-top: ${toRem(theme.space.sm)};
  width: 100%;

  &:not(:last-child) {
    padding-bottom: ${toRem(theme.space.sm)};
    border-bottom: 1px solid ${theme.color.neutral[200]};
  }

  & > *:first-child {
    word-break: break-word;
  }

  @media (max-width: ${WeeklyToDailyBreakpoint}px) {
    flex-flow: column nowrap;

    & > *:not(:last-child) {
      padding-bottom: ${toRem(theme.space.xs)};
    }
  }
`;

const BusinessDayMessage = styled(Text)<{ background?: string }>`
  background-color: ${({ background }) => background || theme.color.neutral[100]};
  border-radius: ${toRem(8)} ${toRem(8)} 0 0;
  text-align: center;
  padding: ${toRem(theme.space.sm)};
  width: 100%;
`;

const ReviewLaborDataWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: ${toRem(theme.space.xl)};
`;

const ReviewLaborDataLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: ${toRem(theme.space['2xs'])};
  font-size: ${toRem(theme.font.size.xs)};
  font-weight: ${theme.font.weight.semiBold};
  line-height: ${lineHeight(theme.font.size.xs)};
  color: ${theme.color.primary[500]};
  text-decoration: none;

  svg {
    width: ${toRem(12)};
    height: ${toRem(12)};
    fill: ${theme.color.primary[500]};
  }
`;

const Footer = styled.div`
  margin-top: ${toRem(theme.space.lg)};
  display: flex;
  flex-direction: column;

  button {
    width: ${toRem(210)};

    &:first-child {
      margin-bottom: ${toRem(theme.space.md)};
    }
  }

  /* Desktop styles */
  @media (min-width: ${theme.breakpoint.small}px) {
    flex-direction: row;

    button {
      &:first-child {
        margin-right: ${toRem(theme.space.md)};
        margin-bottom: 0;
      }
    }
  }
`;

const StyledText = styled(Text)`
  @media (max-width: ${WeeklyToDailyBreakpoint}px) {
    display: none;
  }
`;

interface IDayMessage {
  statusColor: string;
  message: ReactNode;
  dataCy: string;
}

interface IGetDayMessage {
  isDayOver: boolean;
  isDayPaid: boolean;
  isInClosedPayPeriod: boolean;
  isBeforeStartDate: boolean;
  daySegments: IDaySegment[];
}

/**
 * Resolves the warning message shown for a selected day, in priority order.
 *
 * @param {IGetDayMessage} params - the day's payability flags.
 * @returns {IDayMessage | null} the first matching message, or null when no condition matches.
 */
const getDayMessage = ({ isDayOver, isDayPaid, isInClosedPayPeriod, isBeforeStartDate, daySegments }: IGetDayMessage): IDayMessage | null => {
  const messages: { condition: boolean; notification: IDayMessage }[] = [
    {
      condition: !isDayOver,
      notification: { message: 'Tips cannot be issued until the business day has concluded.', statusColor: theme.color.danger[25], dataCy: 'select-date-error' },
    },
    {
      condition: isDayPaid,
      notification: { message: 'You have already issued tips for this day.', statusColor: theme.color.warning[25], dataCy: 'selected-date-paid-status' },
    },
    {
      condition: isInClosedPayPeriod,
      notification: { message: 'This day falls within a closed pay period.', statusColor: theme.color.warning[25], dataCy: 'closed-pay-period-warning' },
    },
    {
      condition: isBeforeStartDate,
      notification: { message: 'Tip calculations cannot be performed prior to the configured start date.', statusColor: theme.color.warning[25], dataCy: 'before-start-date-warning' },
    },
    {
      condition: daySegments.length === 0,
      notification: {
        message: (
          <span>
            Day segments have not been configured for this day.{' '}
            <Link to={routeConstants.private.settingsDaySegments}>
              <b>Click here</b>
            </Link>{' '}
            to configure day segments.
          </span>
        ),
        statusColor: theme.color.danger[25],
        dataCy: 'select-date-segment-error',
      },
    },
  ];

  return messages.find(({ condition }) => condition)?.notification ?? null;
};

interface IDayInfoProps {
  selectedDay?: Dayjs;
  paymentSets?: IPaymentSet[];
  isDayOver: boolean;
  payPeriod?: IPayPeriod;
  isBeforeStartDate: boolean;
  daySegments: IDaySegment[];
}

const DayInfo = ({ selectedDay, paymentSets, isDayOver, payPeriod, isBeforeStartDate, daySegments }: IDayInfoProps) => {
  if (!selectedDay) return null;
  const selectedDate = formatDateForApi(selectedDay);
  const paymentSetForDate = paymentSets?.find((item) => item.paymentDate === selectedDate);
  const isDayPaid = !!paymentSetForDate;
  const isInClosedPayPeriod = isPayPeriodClosed(payPeriod);
  const hasDaySegments = daySegments.length > 0;
  const dayMessage = getDayMessage({ isDayOver, isDayPaid, isInClosedPayPeriod, isBeforeStartDate, daySegments });
  return (
    <Wrapper direction="column" width="100%" gap={0}>
      {dayMessage && (
        <BusinessDayMessage data-cy={dayMessage.dataCy} background={dayMessage.statusColor} size="small" fontWeight="medium">
          {dayMessage.message}
        </BusinessDayMessage>
      )}
      <ContentWrapper hasHeader={!!dayMessage}>
        <SelectedDateContainer style={hasDaySegments ? { marginBottom: toRem(theme.space.lg) } : {}} data-cy="selected-date-header">
          <H3>{selectedDay.format('MMMM Do, YYYY')}</H3>
          {isDayPaid && <Link to={`/payments/payment-set/${paymentSetForDate.id}`}>View the payment set</Link>}
        </SelectedDateContainer>
        {hasDaySegments && (
          <>
            <DaySegmentHeader data-cy="day-segment-header">
              <Text size="xsmall" fontWeight="medium">
                Day Segment
              </Text>
              <StyledText size="xsmall" fontWeight="medium" className="segment-hours">
                Hours
              </StyledText>
            </DaySegmentHeader>
            {daySegments.map((segment) => (
              <DaySegment data-cy="day-segment-info" key={`day-segment-info-${segment.id}`}>
                <Text size="xsmall" fontWeight="medium">
                  {segment.name}
                </Text>
                <Text size="xsmall">{formatTimeRange(segment.start, segment.end)}</Text>
              </DaySegment>
            ))}
          </>
        )}
      </ContentWrapper>
    </Wrapper>
  );
};

const validateBeforeStartDate = (startDate?: string, selectedDay?: Dayjs): boolean => {
  if (!startDate || !selectedDay) return false;
  return selectedDay.isBefore(startDate, 'day');
};

const DateSelector = ({ currentSite, currentSiteId, onNext, pageHeaderContent }: IDateSelectorProps) => {
  const dispatch = useDispatch();
  const { endDayMidnightOffset, timezone, tipCalculatorStartDate } = useSelector(selectSiteSettings()) || {};
  const daySegmentsForSite = useSelector(selectDaySegments());
  const paymentSetsForCalendarDates = useSelector(selectPaymentSetsForSite());
  const isLoadingDaySegments = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, DaySegments.GET_DAY_SEGMENTS_REQUEST));
  const isLoadingCalculatorRules = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, CalculatorRules.GET_CALCULATOR_RULES_REQUEST));
  const isLoadingPaymentsForSite = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Payments.GET_SITE_PAYMENTS_REQUEST));
  const hasCinemarkIntegration = useSelector(selectHasIntegration(IntegrationType.CINEMARK));
  const hasLaborDataViewPermission = hasSitePermission(currentSite?.permissions, PermissionType.INTEGRATION_POS_LABOR_DATA_VIEW);

  const [visibleDays, setVisibleDays] = useState<IWeekView[]>([]);
  const [selectedDay, setSelectedDay] = useState<Dayjs>();
  const isSelectedDayOver = isDatePayable(selectedDay, endDayMidnightOffset, timezone, daySegmentsForSite?.data?.length > 0 ? daySegmentsForSite?.data : undefined);
  const isBeforeStartDate = validateBeforeStartDate(tipCalculatorStartDate, selectedDay);

  useEffect(() => {
    if (currentSite?.id && currentSite.settings?.tipCalculator) {
      dispatch(getDaySegmentsRequest({ siteId: currentSite.id, options: { sort: 'created_at', limit: 0 } }));
    }
  }, [currentSite]);

  const fetchPaymentsForVisibleDays = (days: IWeekView[]) => {
    // fetch site payouts, filter by the visible days in the calendar and `calculator` payment type
    if (days?.length) {
      const startDate = days[0].weekday;
      const endDate = days[days.length - 1].weekday;
      const q = [
        { key: 'ref_type', operator: ApiQueryOperator.eq, value: PayoutRefType.CALCULATOR },
        { key: 'payment_date', operator: ApiQueryOperator.gte, value: formatDateForApi(startDate) },
        { key: 'payment_date', operator: ApiQueryOperator.lte, value: formatDateForApi(endDate) },
      ];
      dispatch(getSitePaymentsRequest(currentSiteId, { q }));
    }
  };

  const mappedVisibleDaysToPaidDays = useMemo(
    () =>
      visibleDays?.map((day) => ({
        ...day,
        includeCheck: paymentSetsForCalendarDates?.some((paidDay) => paidDay.paymentDate === formatDateForApi(day.weekday)) || false,
      })),
    [visibleDays, paymentSetsForCalendarDates]
  );

  const selectedDaySegments = useMemo(() => {
    const selectedDayName = selectedDay?.format('dddd').toLocaleUpperCase() as Days;
    if (!selectedDayName) return [];
    return (
      (daySegmentsForSite?.data || [])
        .filter((daySegment) => daySegment?.days.includes(selectedDayName))
        // sort segments by start time
        .sort((a, b) => {
          const aStart = parseInt(a.start.split(':').join(''), 10);
          const bStart = parseInt(b.start.split(':').join(''), 10);
          return aStart - bStart;
        })
    );
  }, [daySegmentsForSite, selectedDay]);

  const { selectedPayPeriod, isLoadingPayPeriods } = useDayPayPeriod(currentSiteId, visibleDays, selectedDay);

  const disableNext = !selectedDay || !isSelectedDayOver || isBeforeStartDate || !selectedDaySegments.length || isLoadingPayPeriods;

  return (
    <>
      {pageHeaderContent}
      <CalendarWrapper>
        <H3 data-cy="select-date-title" marginBottom={theme.space.lg}>
          Select a day to payout tips
        </H3>
        <DayPicker
          expandedDay={<DayInfo selectedDay={selectedDay} paymentSets={paymentSetsForCalendarDates} isDayOver={isSelectedDayOver} payPeriod={selectedPayPeriod} isBeforeStartDate={isBeforeStartDate} daySegments={selectedDaySegments} />}
          startDate={tipCalculatorStartDate}
          disableFuture
          timezone={timezone}
          isLoading={isLoadingDaySegments || isLoadingCalculatorRules || isLoadingPaymentsForSite || isLoadingPayPeriods}
          onDayClick={(day) => setSelectedDay(day?.weekday)}
          onCalNavigation={(days: IWeekView[]) => {
            setVisibleDays(days);
            fetchPaymentsForVisibleDays(days);
          }}
          customizedVisibleWeek={paymentSetsForCalendarDates?.length ? mappedVisibleDaysToPaidDays : undefined}
          selectedDay={selectedDay}
        />
      </CalendarWrapper>
      <Footer>
        <Button data-cy="back-btn" label="Back" appearance="white" onClick={() => browserHistory.push(routeConstants.private.makePayment)} />
        <Button data-cy="next-btn" label="Continue" appearance="primary" disabled={disableNext} onClick={() => onNext(selectedDay, selectedDaySegments, selectedPayPeriod)} />
      </Footer>
      {!disableNext && hasCinemarkIntegration && hasLaborDataViewPermission && selectedDay && (
        <ReviewLaborDataWrapper>
          <ReviewLaborDataLink data-cy="review-labor-data-btn" to={`${routeConstants.private.laborReview}?${new URLSearchParams({ date: formatDateForApi(selectedDay) })}`}>
            <Icon icon="Pencil" />
            Review Labor Data
          </ReviewLaborDataLink>
        </ReviewLaborDataWrapper>
      )}
    </>
  );
};

export default DateSelector;
